'use client';

import { Check } from 'lucide-react';

import {
  CONFIRM_EMAIL_BUTTON_ID,
  EMAIL_VERIFICATION_COPY,
  PROFILE_EMAIL_SECTION_ID,
} from '../../consts';
import { useProfileEmailSection } from '../../hooks';
import { ProfileEmailAddressRow } from './profile-email-address-row';
import { ProfileEmailChangeForm } from './profile-email-change-form';
import { ProfileEmailCodeForm } from './profile-email-code-form';
import { profileEmailSectionStyles } from './profile-email-section.styles';
import { ProfileEmailStatusPill } from './profile-email-status-pill';

export const ProfileEmailSection = () => {
  const {
    view,
    pill,
    emailDraft,
    passwordDraft,
    emailError,
    passwordError,
    codeValue,
    codeError,
    secondsLeft,
    isSubmitting,
    handleSendCode,
    handleResendCode,
    handleConfirmCode,
    handleStartChange,
    handleCancelChange,
    handleEmailDraftChange,
    handlePasswordDraftChange,
    handleCodeChange,
    handleSubmitChange,
  } = useProfileEmailSection();

  return (
    <section id={PROFILE_EMAIL_SECTION_ID} aria-labelledby="email-heading" className="mt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 id="email-heading" className="text-sm font-medium text-[#888C91]">
          {EMAIL_VERIFICATION_COPY.emailLabel}
        </h2>
        <ProfileEmailStatusPill tone={pill.tone} label={pill.label} />
      </div>

      {view.kind === 'provider' ? (
        <>
          <ProfileEmailAddressRow value={view.value} label={view.label} />
          <p className={profileEmailSectionStyles.helper}>
            {EMAIL_VERIFICATION_COPY.providerHelper}
          </p>
        </>
      ) : null}

      {view.kind === 'verified' ? (
        <>
          <ProfileEmailAddressRow
            value={view.email}
            label={EMAIL_VERIFICATION_COPY.verifiedStatus}
            actionLabel={EMAIL_VERIFICATION_COPY.changeEmail}
            onAction={handleStartChange}
          />
          <p
            className={`${profileEmailSectionStyles.helper} p-2.5 rounded-1xl flex items-start gap-2 bg-[#edf9f1] text-[#266a3e]`}
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{EMAIL_VERIFICATION_COPY.verifiedHelper}</span>
          </p>
        </>
      ) : null}

      {view.kind === 'unverified' ? (
        <>
          <ProfileEmailAddressRow
            value={view.email}
            label={EMAIL_VERIFICATION_COPY.unverifiedStatus}
            actionLabel={EMAIL_VERIFICATION_COPY.changeEmail}
            onAction={handleStartChange}
          />
          <p className={profileEmailSectionStyles.helper}>
            {EMAIL_VERIFICATION_COPY.unverifiedHelper}
          </p>
          <div className="mt-3 flex justify-end">
            <button
              id={CONFIRM_EMAIL_BUTTON_ID}
              type="button"
              onClick={() => void handleSendCode()}
              disabled={isSubmitting}
              className={profileEmailSectionStyles.primary}
            >
              {EMAIL_VERIFICATION_COPY.confirmEmail}
            </button>
          </div>
        </>
      ) : null}

      {view.kind === 'code' ? (
        <ProfileEmailCodeForm
          destination={view.destination}
          codeValue={codeValue}
          codeError={codeError}
          secondsLeft={secondsLeft}
          isSubmitting={isSubmitting}
          onCodeChange={handleCodeChange}
          onConfirm={() => void handleConfirmCode()}
          onResend={() => void handleResendCode()}
          onChangeEmail={handleStartChange}
        />
      ) : null}

      {view.kind === 'change' ? (
        <ProfileEmailChangeForm
          emailDraft={emailDraft}
          passwordDraft={passwordDraft}
          emailError={emailError}
          passwordError={passwordError}
          isSubmitting={isSubmitting}
          onEmailDraftChange={handleEmailDraftChange}
          onPasswordDraftChange={handlePasswordDraftChange}
          onCancel={handleCancelChange}
          onSubmit={() => void handleSubmitChange()}
        />
      ) : null}
    </section>
  );
};
